import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';

const Popover = ({ isOpen, onClose, title, children, targetRef, placement = 'top' }) => {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);

  const arrowClasses = useMemo(() => {
    const arrows = {
      top: 'absolute w-0 h-0 top-full left-4 border-l-8 border-r-8 border-t-8 border-transparent border-t-white',
      bottom: 'absolute w-0 h-0 bottom-full left-4 border-l-8 border-r-8 border-b-8 border-transparent border-b-white',
      left: 'absolute w-0 h-0 left-full top-1/2 transform -translate-y-1/2 border-t-8 border-b-8 border-l-8 border-transparent border-l-white',
      right: 'absolute w-0 h-0 right-full top-1/2 transform -translate-y-1/2 border-t-8 border-b-8 border-r-8 border-transparent border-r-white',
    };
    return arrows[placement] || arrows.top;
  }, [placement]);

  const calculatePosition = useCallback((targetRect, popoverRect, parentRect) => {
    const offset = 16;
    const gap = 12;

    switch (placement) {
      case 'top':
        return {
          top: targetRect.top - parentRect.top - popoverRect.height - gap,
          left: targetRect.left - parentRect.left - offset,
        };
      case 'bottom':
        return {
          top: targetRect.bottom - parentRect.top + gap,
          left: targetRect.left - parentRect.left - offset,
        };
      case 'left':
        return {
          top: targetRect.top - parentRect.top + (targetRect.height - popoverRect.height) / 2,
          left: targetRect.left - parentRect.left - popoverRect.width - offset,
        };
      case 'right':
        return {
          top: targetRect.top - parentRect.top + (targetRect.height - popoverRect.height) / 2,
          left: targetRect.right - parentRect.left + 8,
        };
      default:
        return {
          top: targetRect.top - parentRect.top - popoverRect.height - gap,
          left: targetRect.left - parentRect.left - offset,
        };
    }
  }, [placement]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen && targetRef?.current && popoverRef.current && !isMobile) {
      const targetRect = targetRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const parentRect = targetRef.current.offsetParent?.getBoundingClientRect() || { top: 0, left: 0 };

      const newPosition = calculatePosition(targetRect, popoverRect, parentRect);
      setPosition(newPosition);
      setIsPositioned(true);
    } else if (!isOpen) {
      setIsPositioned(false);
    }
  }, [isOpen, placement, targetRef, isMobile, calculatePosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)
          && targetRef?.current && !targetRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, targetRef]);

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[351] flex items-end justify-center bg-black bg-opacity-50">
        <div
          ref={popoverRef}
          className="w-full max-w-md bg-white rounded-t-lg shadow-lg"
        >
          {title && (
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
          )}
          <div className="px-4 py-3 max-h-80 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={popoverRef}
      className={`absolute z-50 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-w-sm transition-opacity duration-200 ${
        isPositioned ? 'opacity-100' : 'opacity-0'
      }`}
      style={position}
    >
      {title && (
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
      )}
      <div className="px-4 py-3 max-h-64 overflow-y-auto">
        {children}
      </div>
      <div className={arrowClasses} />
    </div>
  );
};

Popover.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  targetRef: PropTypes.object.isRequired,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
};

Popover.defaultProps = {
  title: '',
  placement: 'top',
};

export default Popover;
